/**
 * A tela de Aparelhos: sessoes, aparelhos, credenciais e tokens.
 *
 * **Esta tela mexe em credencial, entao tudo que muda estado passa por
 * reautenticacao** -- a API exige, e o `comReautenticacao` cuida disso sem
 * que cada botao precise saber.
 *
 * **A senha MQTT aparece uma unica vez**, e a tela diz isso duas vezes: no
 * aviso que a API devolve e no titulo do bloco. A senha nao vai para o
 * `localStorage`, nao vai para a URL e nao sobrevive ao redesenho da tela -- ela
 * fica em uma variavel, e some quando a pessoa fecha.
 */

import {
  ErroDaApi,
  aparelhos as buscarAparelhos,
  cadastrarAparelho,
  chaveVapid,
  definirAtalhos,
  gerarCredencialMqtt,
  registrarPush,
  revogarAparelho,
  revogarSessao,
  sessoes as buscarSessoes,
} from "./api";
import { comReautenticacao } from "./reauth";
import { telaDeAparelhos, telaDeAtalhos, telaDaCredencial, texto } from "./telas";
import type { AparelhoParaTela, SessaoParaTela } from "./telas";
import type { Aparelho } from "./api";

/** Onde a tela desenha. O `main` passa o mesmo elemento que usa para o resto. */
let raiz: HTMLElement | null = null;
/** A credencial MQTT em claro, enquanto a pessoa a anota. */
let credencial: { usuario: string; senha: string; aviso: string } | null = null;
/**
 * A lista como a API a mandou.
 *
 * **E preciso guardar, e nao reler do DOM.** O formulario de atalhos precisa do
 * `trigger_email` e do `trigger_phone` **reais** -- e o DOM tem o botao e o
 * texto, nao o dado. Ler do DOM seria reinventar o objeto a partir do que ele
 * desenhou, e errar no primeiro campo que a tela nao mostra.
 */
let listaAtual: AparelhoParaTela[] = [];

export function definirRaiz(elemento: HTMLElement): void {
  raiz = elemento;
}

function aviso(erro: unknown): void {
  const area = raiz?.querySelector<HTMLElement>("#aviso-da-tela");
  if (area === null || area === undefined) {
    return;
  }
  if (erro instanceof ErroDaApi) {
    area.hidden = false;
    area.textContent = erro.message;
    return;
  }
  area.hidden = false;
  area.textContent = "Algo deu errado. Tente de novo.";
}

/** Desenha a tela inteira, com os dados mais recentes. */
export async function desenhar(): Promise<void> {
  if (raiz === null) {
    return;
  }
  const [sessoes, lista] = await Promise.all([buscarSessoes(), buscarAparelhos()]);
  const sessaoParaTela: SessaoParaTela[] = sessoes.map((sessao) => ({
    id: sessao.id,
    criada_em: sessao.criada_em,
    ultimo_uso_em: sessao.ultimo_uso_em,
    expira_em: sessao.expira_em,
    esta_atual: sessao.esta_atual,
  }));
  const aparelhoParaTela: AparelhoParaTela[] = lista.map((aparelho: Aparelho) => ({
    id: aparelho.id,
    platform: aparelho.platform,
    label: aparelho.label,
    mqtt_username: aparelho.mqtt_username,
    tem_credencial_mqtt: aparelho.tem_credencial_mqtt,
    tem_token_de_atalhos: aparelho.tem_token_de_atalhos,
    trigger_email: aparelho.trigger_email,
    trigger_phone: aparelho.trigger_phone,
    compartilhamento_pausado: aparelho.compartilhamento_pausado,
    revogado_em: aparelho.revogado_em,
    push_configurado: aparelho.push_configurado,
  }));
  listaAtual = aparelhoParaTela;

  raiz.innerHTML = telaDeAparelhos(sessaoParaTela, aparelhoParaTela);
  // **Esta tela nao e centralizada**: a lista de aparelhos e larga, e o limite
  // de largura das telas de acesso deixa a lista ilegivel num tablet.
  raiz.classList.remove("conteudo--centrado");
  liga();
  if (credencial !== null) {
    mostraCredencial();
  }
}

/** Liga os botoes da tela. **Toda vez que a tela e desenhada.** */
function liga(): void {
  if (raiz === null) {
    return;
  }

  for (const botao of raiz.querySelectorAll<HTMLButtonElement>("[data-revogar-sessao]")) {
    botao.addEventListener("click", () => {
      const id = botao.dataset["revogarSessao"] ?? "";
      void executa(async () => {
        await revogarSessao(id);
        await desenhar();
      });
    });
  }

  for (const botao of raiz.querySelectorAll<HTMLButtonElement>("[data-gerar-mqtt]")) {
    botao.addEventListener("click", () => {
      const id = botao.dataset["gerarMqtt"] ?? "";
      void executa(async () => {
        const gerada = await gerarCredencialMqtt(id);
        credencial = { usuario: gerada.mqtt_username, senha: gerada.senha, aviso: gerada.aviso };
        await desenhar();
      });
    });
  }

  for (const botao of raiz.querySelectorAll<HTMLButtonElement>("[data-atalhos]")) {
    botao.addEventListener("click", () => {
      const id = botao.dataset["atalhos"] ?? "";
      const aparelho = aparelhoComId(id);
      if (aparelho === null) {
        return;
      }
      const linha = raiz?.querySelector<HTMLElement>(`[data-aparelho="${id}"]`);
      if (linha === null || linha === undefined) {
        return;
      }
      const area = linha.querySelector<HTMLElement>(".area-dos-atalhos");
      if (area !== null) {
        area.remove();
        return;
      }
      const holder = document.createElement("div");
      holder.className = "area-dos-atalhos";
      holder.innerHTML = telaDeAtalhos(id, aparelho.trigger_email, aparelho.trigger_phone);
      linha.appendChild(holder);
      ligaFormularioDeAtalhos(holder, id);
    });
  }

  for (const botao of raiz.querySelectorAll<HTMLButtonElement>("[data-push]")) {
    botao.addEventListener("click", () => {
      const id = botao.dataset["push"] ?? "";
      void executa(async () => {
        await ativarNotificacoes(id);
        await desenhar();
      });
    });
  }

  for (const botao of raiz.querySelectorAll<HTMLButtonElement>("[data-revogar-aparelho]")) {
    botao.addEventListener("click", () => {
      const id = botao.dataset["revogarAparelho"] ?? "";
      void executa(async () => {
        await revogarAparelho(id);
        await desenhar();
      });
    });
  }

  const forma = raiz.querySelector<HTMLFormElement>("#forma-novo-aparelho");
  forma?.addEventListener("submit", (evento) => {
    evento.preventDefault();
    const rotulo = raiz?.querySelector<HTMLInputElement>("#rotulo-do-aparelho")?.value.trim() ?? "";
    const plataforma = raiz?.querySelector<HTMLSelectElement>("#plataforma-do-aparelho")?.value ?? "android";
    void executa(async () => {
      await cadastrarAparelho(plataforma, rotulo);
      await desenhar();
    });
  });
}

function ligaFormularioDeAtalhos(holder: HTMLElement, deviceId: string): void {
  const forma = holder.querySelector<HTMLFormElement>("[data-atalhos-de]");
  forma?.addEventListener("submit", (evento) => {
    evento.preventDefault();
    const email = holder.querySelector<HTMLInputElement>("#email-do-atalho")?.value.trim() ?? "";
    const telefone = holder.querySelector<HTMLInputElement>("#telefone-do-atalho")?.value.trim() ?? "";
    void executa(async () => {
      await definirAtalhos(deviceId, email === "" ? null : email, telefone === "" ? null : telefone);
      await desenhar();
    });
  });
  holder.querySelector<HTMLButtonElement>("#cancelar-atalhos")?.addEventListener("click", () => {
    holder.remove();
  });
}

function aparelhoComId(id: string): AparelhoParaTela | null {
  return listaAtual.find((aparelho) => aparelho.id === id) ?? null;
}

/**
 * Ativa as notificacoes do aparelho.
 *
 * **A chave VAPID vem do servidor**, e o `503` dela (sem chaves no `.env`) e
 * traduzido em "indisponivel aqui" -- e nao em erro. A tela de Aparelhos e
 * usada em desenvolvimento, onde nao ha chave de push, e um erro vermelho
 * nessa tela seria ruido.
 */
async function ativarNotificacoes(deviceId: string): Promise<void> {
  const publica = await chaveVapid();
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    throw new ErroDaApi(0, "Este navegador nao manda notificacoes.", null);
  }
  const registro = await navigator.serviceWorker.ready;
  const assinatura = await registro.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: publica,
  });
  await registrarPush(deviceId, assinatura.toJSON());
}

function mostraCredencial(): void {
  if (raiz === null || credencial === null) {
    return;
  }
  const area = raiz.querySelector<HTMLElement>("#area-da-credencial");
  if (area === null) {
    return;
  }
  area.hidden = false;
  area.innerHTML = telaDaCredencial(credencial.usuario, credencial.senha, credencial.aviso);
  area.querySelector<HTMLButtonElement>("#fechar-credencial")?.addEventListener("click", () => {
    // **A senha some da tela e da memoria.** Ela existiu uma vez, e a pessoa a
    // anotou -- de novo nao ha, porque a API nao devolve.
    credencial = null;
    area.hidden = true;
    area.innerHTML = "";
  });
}

/** Roda uma acao, com reautenticacao, e mostra o erro na tela. */
async function executa(acao: () => Promise<void>): Promise<void> {
  try {
    await comReautenticacao(acao);
  } catch (erro) {
    aviso(erro);
  }
}

export { texto };
